import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { NGXLogger } from 'ngx-logger';
import { isPlatformBrowser } from '@angular/common';
import { IAnalyticsService } from './analytics.interface';
import { Order, Product, ProductVariant } from '../../../common/generated-types';
import { GTM_EVENT_TIMEOUT } from '../../../common/constants';
import { QueryParamService } from '../query-param/query-param.service';

enum GTMInitState {
    Loading = 'Loading',
    Initialized = 'Initialized',
    Failed = 'Failed',
    NoBrower = 'NoBrower',
    Disabled = 'disabled',
}

interface DataLayerEvent {
    event?: string;
    [key: string]: any;
}

declare global {
    interface Window {
        dataLayer: DataLayerEvent[];
        gtag: (...args: unknown[]) => void;
    }
}

// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm

@Injectable({
    providedIn: 'root'
})
export class GTMService implements IAnalyticsService {
    private email = '';
    private gaInitState: GTMInitState = GTMInitState.Loading;
    private eventsQueue: any[] = [];
    private static readonly MAX_QUEUE_SIZE = 100;

    constructor(
        private logger: NGXLogger,
        @Inject(PLATFORM_ID) private platformId: object,
        private queryParamService: QueryParamService
    ) { }


    private async loadGtmScript(gtmId: string, gtmModeIsServerSide: boolean): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!isPlatformBrowser(this.platformId)) {
                return resolve(); // SSR: no-op
            }

            if (document.getElementById('gtm-script')) {
                return resolve(); // Already loaded
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js',
            });

            const script = document.createElement('script');
            script.async = true;
            if (gtmModeIsServerSide) {
                // Base64 encode the gtmId for obfuscation (matches SSR logic)
                const encodedGtmId = btoa(gtmId);
                script.src = `ljs/cdm.js?id=${encodedGtmId}`;
            } else {
                // Default GTM script from Google
                script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
            }
            script.id = 'gtm-script';
            script.onload = () => resolve();
            script.onerror = (err) => reject(err);

            document.head.appendChild(script);
        });
    }


    public async initialize(options: any): Promise<void> {
        const { gtmId, gtmModeIsServerSide } = options;
        if (isPlatformBrowser(this.platformId)) {

            try {
                // If SSR already injected the script tag, don't attempt to load again.
                if (document.getElementById('gtm-script')) {
                    this.logger.debug('[GTMService] GTM script already present, skipping load', gtmId);
                } else {
                    await this.loadGtmScript(gtmId, gtmModeIsServerSide ?? false);
                }
                this.gaInitState = GTMInitState.Initialized;
                this.processQueue();
                this.logger.debug('[GTMService] GTM script loaded', gtmId);
            } catch (error) {
                this.logger.error('[GTMService] login event error', error);
                this.gaInitState = GTMInitState.Failed;
                this.dumpQueue();
            }
        } else {
            this.gaInitState = GTMInitState.NoBrower;
        }
    }

    public disabled(): void {
        this.gaInitState = GTMInitState.Disabled;
        this.dumpQueue();
    }

    private processQueue(): void {
        while (this.eventsQueue.length > 0) {
            const eventFn = this.eventsQueue.shift();
            eventFn();
        }
    }

    private dumpQueue(): void {
        this.eventsQueue = [];
    }

    private queueEvent(eventFn: () => void): void {
        this.logger.debug('[queueEvent] Queuing event function');
        this.eventsQueue.push(eventFn);
        if (this.eventsQueue.length > GTMService.MAX_QUEUE_SIZE) {
            this.logger.warn('[queueEvent] Queue size exceeded, dumping oldest event');
            this.eventsQueue.shift();
        }
    }

    public setEmail(email: string): void {
        if (email !== this.email) {
            if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
                return;
            }
            this.email = email;
            const emailHash = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex);
            this.sendGTMEvent('set_user_data', { sha256_email_address: emailHash }, undefined);
        }
    }

    private sendGTMEvent(event: any, eventData: any, callback: any = undefined): void {
        if (this.gaInitState === GTMInitState.Initialized) {
            if (typeof window !== 'undefined') {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ 'ecommerce': null }); // Clear the previous ecommerce object.
                window.dataLayer.push({
                    'event': event,
                    'ecommerce': eventData,
                    'eventCallback': callback,
                    'eventTimeout': GTM_EVENT_TIMEOUT,
                    'utm_params': this.queryParamService.getUtmInfo(),
                });
            }
        } else if (this.gaInitState === GTMInitState.Loading) {
            this.queueEvent(() => this.sendGTMEvent(event, eventData));
        }
        // callback should not be blocked in loading state
        if (callback) {
            callback();
        }
    }

    public pageView(pageType: string, extra: Record<string, any>): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            const payload = {
                event: 'page_view',
                page_type: pageType,
                page_path: window.location.pathname + window.location.search + window.location.hash,
                page_location: window.location.href,
                ...extra,
            };
            window.dataLayer.push(payload);
        }
    }

    public selectItem(product: Product, variant: ProductVariant): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            return;
        }
        const product_event = {
            item_list_id: product?.slug,
            item_list_name: product?.name,
            items: [
                {
                    item_id: variant?.sku,
                    item_name: variant?.name,
                    index: 0,
                    item_list_id: product?.slug,
                    item_list_name: product?.name,
                    item_variant: variant?.options?.map((option: any) => option.name).join(', '),
                    price: variant.price / 100.0,
                    quantity: 1,
                }
            ]
        };
        this.sendGTMEvent('select_item', product_event);
    }

    public viewItem(product: Product, variant: ProductVariant): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            return;
        }
        const product_event = {
            currency: 'USD',
            value: variant.price / 100.0,
            items: [
                {
                    item_id: variant?.sku,
                    item_name: variant?.name,
                    index: 0,
                    item_list_id: product?.slug,
                    item_list_name: product?.name,
                    item_variant: variant?.options?.map((option: any) => option.name).join(', '),
                    price: variant.price / 100.0,
                    quantity: 1,
                }
            ]
        };
        this.sendGTMEvent('view_item', product_event);
    }

    public addToCart(product: Product, variant: ProductVariant, quantity: number): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            return;
        }
        const variant_event = {
            currency: 'USD',
            value: variant.price * quantity / 100.0,
            items: [
                {
                    item_id: variant?.sku,
                    item_name: variant?.name,
                    index: 0,
                    item_list_id: product?.slug,
                    item_list_name: product?.name,
                    item_variant: variant?.options?.map((option: any) => option.name).join(', '),
                    price: variant.price / 100.0,
                    quantity: quantity,
                }
            ],
        };
        this.sendGTMEvent('add_to_cart', variant_event);
    }

    public beginCheckout(order: Order, callback: any): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            if (callback) {
                callback();
            }
            return;
        }
        const order_event = {
            currency: 'USD',
            value: order?.totalWithTax / 100.0,
            coupon: order?.couponCodes?.join(', '),
            items: order?.lines?.map((line: any, index: number) => {
                return {
                    item_id: line?.productVariant?.sku,
                    item_name: line?.productVariant?.name,
                    index: index,
                    item_list_id: line?.productVariant?.product?.slug,
                    item_list_name: line?.productVariant?.product?.name,
                    item_variant: line?.productVariant?.options?.map((option: any) => option.name).join(', '),
                    price: line?.unitPriceWithTax / 100.0,
                    quantity: line?.quantity,
                    discount: (line?.discounts.map((discount: any) => discount?.amountWithTax).reduce((a: number, b: number) => a + b, 0)) / 100.0,
                    coupon: line?.discounts.map((discount: any) => discount?.description).join(', '),
                };
            }),
        };
        this.sendGTMEvent('begin_checkout', order_event, callback);
    }

    public addShippingInfo(order: Order, callback: any): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            if (callback) {
                callback();
            }
            return;
        }
        const order_event = {
            currency: 'USD',
            value: order?.totalWithTax / 100.0,
            tax: (order?.subTotalWithTax - order?.subTotal) / 100.0,
            shipping_tier: order?.shippingLines?.[0]?.shippingMethod?.name,
            coupon: order?.couponCodes.join(', '),
            items: order?.lines?.map((line: any, index: number) => {
                return {
                    item_id: line.productVariant?.sku,
                    item_name: line.productVariant?.name,
                    index: index,
                    item_list_id: line.productVariant?.product?.slug,
                    item_list_name: line.productVariant?.product?.name,
                    item_variant: line.productVariant?.options?.map((option: any) => option.name).join(', '),
                    price: line.unitPriceWithTax / 100.0,
                    quantity: line.quantity,
                    discount: (line.discounts.map((discount: any) => discount.amountWithTax).reduce((a: number, b: number) => a + b, 0)) / 100.0,
                    coupon: line.discounts.map((discount: any) => discount.description).join(', '),
                };
            }),
        };
        this.sendGTMEvent('add_shipping_info', order_event, callback);
    }

    public purchase(order: Order, callback: any): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            if (callback) {
                callback();
            }
            return;
        }
        const order_event = {
            transaction_id: order?.code,
            value: order?.totalWithTax / 100.0,
            tax: (order?.subTotalWithTax - order?.subTotal) / 100.0,
            shipping: order?.shippingWithTax / 100.0,
            coupon: order?.couponCodes?.join(', '),
            currency: 'USD',
            items: order?.lines?.map((line: any, index: number) => {
                return {
                    item_id: line.productVariant?.sku,
                    item_name: line.productVariant?.name,
                    index: index,
                    item_list_id: line.productVariant?.product?.slug,
                    item_list_name: line.productVariant?.product?.name,
                    item_variant: line.productVariant?.options?.map((option: any) => option.name).join(', '),
                    price: line.unitPriceWithTax / 100.0,
                    quantity: line.quantity,
                    discount: (line.discounts.map((discount: any) => discount.amountWithTax).reduce((a: number, b: number) => a + b, 0)) / 100.0,
                    coupon: line.discounts.map((discount: any) => discount.description).join(', '),
                };
            }),
        };
        this.sendGTMEvent('purchase', order_event, callback);
    }

    public validate_address_button(): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            return;
        }
        const order_event = {
            currency: 'USD',
        };
        this.sendGTMEvent('validate_address_button', order_event);
    }

    public complete_order_button(): void {
        if (this.gaInitState === GTMInitState.NoBrower || this.gaInitState === GTMInitState.Disabled) {
            return;
        }
        const order_event = {
            currency: 'USD',
        };
        this.sendGTMEvent('complete_order_button', order_event);
    }
}
